import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select,
  Table
} from 'antd';
import {
  DeleteButton,
  TextField,
  useForm,
  useSelect,
  useTable
} from '@refinedev/antd';
import { IClassroomEvaluationEnrollment } from 'interfaces/classroom_evaluation_enrrolment';
import { useCreate } from '@refinedev/core';

export interface ReplacementAssociateStudentModalProps {
  modalProps: ModalProps;
  modalClose: () => void;
  /** Resource listing candidate students (classroom enrollments). */
  resource: string;
  resourceMeta?: Record<string, unknown>;
  parentId: string;
  /**
   * Resource path for the CRUD of the evaluation-enrollment associations.
   * Defaults to the admin path — override for other namespaces (e.g.
   * `admin/professor_area/classrooms/:id/evaluations/:id/enrollments`).
   */
  enrollmentResource?: string;
  enrollmentResourceMeta?: Record<string, unknown>;
  /**
   * Field path used as the dropdown label. Admin returns the nested
   * `course_enrollment.user.email`; professor_area returns the flat
   * `student_name`.
   */
  optionLabel?: string;
  /** Filter key used by `onSearch`. Must match a `has_scope` on the backend. */
  searchFilterField?: string;
}

const ReplacementAssociateStudentModal = ({
  modalProps,
  resource,
  resourceMeta,
  parentId,
  enrollmentResource: enrollmentResourceProp,
  enrollmentResourceMeta,
  optionLabel = 'course_enrollment.user.email',
  searchFilterField = 'filter[by_user_email]'
}: ReplacementAssociateStudentModalProps) => {
  const enrollmentResource =
    enrollmentResourceProp ?? `classrooms/evaluations/${parentId}/enrollments`;
  const { mutateAsync } = useCreate();
  const { form, formProps } = useForm();
  const classroomEnrollmentId = Form.useWatch('classroom_enrollment_id', form);
  const justification = Form.useWatch('justification', form);
  const isFormValid =
    Boolean(classroomEnrollmentId) && Boolean(justification?.trim());
  const { tableProps, tableQueryResult } =
    useTable<IClassroomEvaluationEnrollment>({
      resource: enrollmentResource,
      meta: enrollmentResourceMeta
    });
  const { selectProps } = useSelect({
    resource,
    meta: resourceMeta,
    optionLabel,
    optionValue: 'id',
    fetchSize: 50,
    onSearch: value => [
      {
        field: searchFilterField,
        operator: 'eq',
        value: value
      }
    ]
  });

  const getOptions = () => {
    const data = tableQueryResult.data?.data;

    if (!data) {
      return [];
    }

    const associatedEnrollments = data.map(obj => obj.classroom_enrollment.id);
    const filteredOptions = selectProps.options?.filter(
      obj => !associatedEnrollments?.includes(obj.value as string)
    );

    return filteredOptions || [];
  };

  const handleCreateAssociation = async () => {
    return form.validateFields().then(async () => {
      await mutateAsync({
        resource: enrollmentResource,
        meta: enrollmentResourceMeta,
        values: {
          ...form.getFieldsValue()
        },
        successNotification: () => ({
          type: 'success',
          description: 'Sucesso!',
          message: 'Aluno associado com sucesso!'
        }),
        errorNotification: () => ({
          type: 'error',
          description: 'Ops!',
          message: 'Não foi possível associar o aluno. Tente novamente.'
        })
      }).finally(() => {
        form.resetFields();
      });
    });
  };

  return (
    <Modal
      {...modalProps}
      title="Associação de alunos"
      className="!w-[1022px]"
      destroyOnClose
      footer={<></>}
    >
      <Form form={form} {...formProps} layout="vertical">
        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Aluno"
              name="classroom_enrollment_id"
              rules={[
                {
                  required: true,
                  message: 'Aluno é obrigatório'
                }
              ]}
            >
              <Select {...selectProps} options={getOptions()} />
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Justificativa"
              name="justification"
              rules={[
                {
                  required: true,
                  message: 'Justificativa é obrigatória'
                }
              ]}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col md={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              className="mb-6"
              type="primary"
              disabled={!isFormValid}
              onClick={() => handleCreateAssociation()}
            >
              Adicionar aluno
            </Button>
          </Col>
        </Row>
      </Form>
      <Table {...tableProps}>
        <Table.Column
          dataIndex={[
            'classroom_enrollment',
            'course_enrollment',
            'user',
            'name'
          ]}
          key="user"
          title="Aluno"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="justification"
          key="justification"
          title="Justificativa"
          render={value => <TextField value={value} />}
        />
        <Table.Column<IClassroomEvaluationEnrollment>
          dataIndex="evaluation-actions"
          key="id"
          title="Ações"
          render={(_, record) => {
            return (
              <Row className="flex gap-2">
                <DeleteButton
                  danger={false}
                  hideText
                  size="small"
                  resourceNameOrRouteName={enrollmentResource}
                  recordItemId={record.id}
                  errorNotification={() => ({
                    type: 'error',
                    description: 'Ops!',
                    message:
                      'Não foi possível desassociar o aluno. Tente novamente.'
                  })}
                  successNotification={() => ({
                    type: 'success',
                    description: 'Sucesso!',
                    message: 'Aluno desassociado com sucesso!'
                  })}
                />
              </Row>
            );
          }}
        />
      </Table>
    </Modal>
  );
};

export default ReplacementAssociateStudentModal;
